import './QualitativeSampleEditForm.css';

import React, { useEffect, useState } from 'react';
import { useLoginContext } from '../../../../App';
import { useContentContext } from '../../../layout/Layout';
import { B2BDatePicker } from '../dates/DatesRangeEditor';
import { generateKeyValueArray, generateSelectField } from '../../../b2b-common/helpers';
import B2BFormContainer from '../../../b2b-common/B2BFormContainer';
import { deleteSample, saveNewSample, updateSample } from '../../../../services/b2b-plans-apiService';
import { showNotification } from '../../../b2b-common/Notification';
import useB2BTranslation from '../../../b2b-common/hooks/useB2BTranslation';

const QualitativeSampleEditForm = ({ sample, planUUID, areaId, areaIndicatorId, indicator, disabledDateRanges, onCancel, onSave, onDelete }) => {
    const { t } = useB2BTranslation('b2b-plans');
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();

    const [currentSample, setCurrentSample] = useState(null);
    const [currentQualitativeType, setCurrentQualitativeType] = useState(null);

    useEffect(() => {
        if (indicator) {
            setCurrentQualitativeType(indicator.qualitativeTypeDTO);
            if (sample) {
                setCurrentSample(sample);
            } else {
                setCurrentSample({
                    sampleType: "QUALITATIVE",
                    sampleId: 0,
                    sampleDate: null,
                    sampleValue: null,
                });
            }
        }
    }, [indicator, sample]);

    const handleSave = async () => {
        if (sample === null) { // create new sample
            const newSample = { ...currentSample, sampleId: 0 };
            try {
                const savedSample = await saveNewSample(loginData.accessToken, planUUID, areaId, areaIndicatorId, newSample);
                showNotification(setNotification, t('Sample created'), 'success', 3000);
                onSave && onSave(savedSample);
            } catch (error) {
                console.error('Error saving new sample:', error);
                showNotification(setNotification, t('Error saving new sample') + ': ' + error.message, 'error', 5000);
            }
        } else { // update existing sample
            try {
                const updatedSample = await updateSample(loginData.accessToken, planUUID, areaId, areaIndicatorId, sample.sampleId, currentSample);
                showNotification(setNotification, t('Sample updated'), 'success', 3000);
                onSave && onSave(updatedSample);
            } catch (error) {
                console.error('Error updating sample:', error);
                showNotification(setNotification, t('Error updating sample') + ': ' + error.message, 'error', 5000);
            }
        }
    }

    const handleDelete = async () => {
        try {
            await deleteSample(loginData.accessToken, planUUID, areaId, areaIndicatorId, sample.sampleId);
            showNotification(setNotification, t('Sample deleted'), 'success', 3000);
            onDelete && onDelete(sample);
        } catch (error) {
            console.error('Error deleting sample:', error);
            showNotification(setNotification, t('Error deleting sample') + ': ' + error.message, 'error', 5000);
        }
    }

    const handleCancel = () => {
        onCancel && onCancel();
    }

    const handleChangeValue = (event) => {
        const weightedValue = currentQualitativeType.weightedValues.find(wv => wv.qualitativeWeightedValueId === event.target.value);
        setCurrentSample({
            ...currentSample,
            sampleValue: {
                qualitativeTypeId: weightedValue.qualitativeTypeId,
                qualitativeWeightedValueId: weightedValue.qualitativeWeightedValueId,
                qualitativeValue: weightedValue.label,
                qualitativeWeight: weightedValue.weight,
            }
        });
    }

    return (
        <B2BFormContainer
            title={t('Edit Qualitative Sample')}
            buttonsConfig={[
                { label: t('Save'), onClick: handleSave },
                ...(sample !== null && sample.objectiveId !== 0 ? [{
                    label: t('Delete'),
                    confirm: true,
                    onConfirm: handleDelete,
                }] : []),
                { label: t('Cancel'), className: 'b2b-button-cancel white_03', onClick: handleCancel },
            ]}
            showDivider={false}
            className="qualitative-sample-edit-form b2b-form-01"
        >
            {currentSample && (
                <>
                    <B2BDatePicker
                        objectHoldingDate={currentSample}
                        setObjectHoldingDate={setCurrentSample}
                        objectDateField={'sampleDate'}
                        label={t('Sample Date')}
                        value={currentSample.sampleDate}
                        className={'qualitative-sample-edit-form-date'}
                        showTime={true}
                        readOnly={sample && sample.sampleId > 0}
                    />
                    {generateSelectField(generateKeyValueArray(currentQualitativeType.weightedValues, 'qualitativeWeightedValueId', 'label'),
                        {
                            label: t('Value'),
                            value: currentSample.sampleValue ? currentSample.sampleValue.qualitativeWeightedValueId : '',
                            className: 'qualitative-sample-edit-form-value', onChange: handleChangeValue
                        })}
                </>
            )}
        </B2BFormContainer>
    );

}

export default QualitativeSampleEditForm;
